import { CloseRounded } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
 
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import SearchIcon from 'common/customIcons/search';
import MDButton from 'components/MDButton';
import { AgGridTable } from 'components/tables/AgGridTable';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CommonLayout from 'layouts/DashboardLayout';
import { Link, useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import EditIcon from 'common/customIcons/edit';
import DeleteIcon from 'common/customIcons/delete';
import { useSelector, useDispatch } from 'react-redux';
import { deleteBrand, fetchBrands, syncBrand } from '../../../redux/slices/hyperwolfBrand';
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from '../../../redux/slices/modalSlice';
import { openSnackbar } from '../../../redux/slices/common';
import FilterBrands from 'common/CommonDrawer/brands/FilterBrands';
import { useDebounce } from 'hooks/useDebounce';
import StatusText from 'common/component/StatusText';
import { withStyles } from '@mui/styles';
import { getData, setData } from 'utilities/common';

const BASE_PATH = '/hyperwolf/brands';

export default function HyperwolfBrands() {
  const { isLoading: loading = false, total = 0, syncBrandLoader = false } = useSelector(
    (state) => state.hyperwolfBrand
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchTermRef = useRef('');
  const [searchTerm, setSearchTerm] = useState('');
  const brandFiltersPayload = useRef({});
  const sortRef = useRef({ sortBy: 'brandName', order: 'desc' });
  const mainGridApiRef = useRef('');
  const pageRef = useRef(0);
  const gridRefApi = useRef(null);
  const [waitingTime, setWaitingTime] = useState('');
  const waitingTimeInterval = useRef(null);

  const LightTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11,
    },
  }))(Tooltip);

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (loading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (total === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      } else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [total, loading]);

  const columnDefs = [
    {
      headerName: 'Brands Name',
      field: 'brandName',
      suppressMenu: false,
      minWidth: 180,
    },
    {
      headerName: 'Brand Website URL',
      field: 'websiteUrl',
      suppressMenu: false,
      minWidth: 200,
    },
    {
      headerName: 'Phone Number',
      field: 'salesRepresentativePhone',
      suppressMenu: false,
      minWidth: 140,
      valueGetter: (params) => {
        const phone = params.data?.salesRepresentativePhone;
        if (!phone) return '-';
        const digits = String(phone).replace(/\D/g, '');
        return digits.length === 10 ? `+1 ${phone}` : phone;
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      suppressMenu: false,
      minWidth: 100,
      cellRenderer: (props) => {
        const status = props?.data?.status;
        if (status === undefined || status === null) return '-';
        const statusLabel = status === true || status === 'active' ? 'active' : 'inactive';
        return <StatusText status={statusLabel} />;
      },
    },
    {
      headerName: 'Action',
      suppressMenu: true,
      cellRenderer: (props) => {
        const id = props?.data?._id || props?.data?.brandId;
        const brandIdForDelete = props?.data?.brandId;
        if (!id) return null;
        return (
          <Box display="inline-flex" alignItems="center" gap={0.5}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onClickEdit(props);
              }}
              color="primary"
              size="small"
            >
              <EditIcon width="16px" height="16px" />
            </IconButton>
            <IconButton
              disabled={!brandIdForDelete}
              onClick={(e) => {
                e.stopPropagation();
                if (!brandIdForDelete) return;
                dispatch(
                  openCustomModalDialog({
                    modalData: {
                      deleteHandler: () => deleteHandler(brandIdForDelete),
                      deleteId: brandIdForDelete,
                      deleteType: 'Brand',
                    },
                    modal: 'DELETE_MODAL',
                  })
                );
              }}
              color="error"
              size="small"
            >
              <DeleteIcon width="16px" height="16px" />
            </IconButton>
          </Box>
        );
      },
      cellStyle: { textAlign: 'right', justifyContent: 'end' },
      headerClass: 'right-aligned-cell-header',
      minWidth: 120,
      pinned: 'right',
    },
  ];

  const getRows = useCallback(
    async (params) => {
      gridRefApi.current = params;
      const filterPayload = brandFiltersPayload.current?.queryPayload || {};
      const sortModel = params?.sortModel?.[0];
      const nextSortBy = sortModel?.colId || sortRef.current.sortBy || 'brandName';
      const nextOrder =
        (sortModel?.sort || sortRef.current.order || 'desc').toLowerCase() === 'asc' ? 'asc' : 'desc';
      if (sortRef.current.sortBy !== nextSortBy || sortRef.current.order !== nextOrder) {
        sortRef.current = { sortBy: nextSortBy, order: nextOrder };
        pageRef.current = 0;
      }
      dispatch(
        fetchBrands({
          skip: pageRef.current,
          searchTerm: searchTermRef.current ?? '',
          limit: 20,
          sortBy: nextSortBy,
          order: sortModel?.sort ? nextOrder : 'asc',
          ...filterPayload,
        })
      )
        .unwrap()
        .then((res) => {
          const list = res?.data ?? [];
          const totalCount = res?.total ?? 0;
          const rows = (Array.isArray(list) ? list : []).map((item) => ({
            ...item,
            id: item._id || item.brandId,
          }));
          params.successCallback(rows, totalCount);
          pageRef.current = pageRef.current + 20;
          if (totalCount === 0 && mainGridApiRef?.current) {
            mainGridApiRef.current.showNoRowsOverlay();
          }
        })
        .catch(() => {
          params.successCallback([], 0);
          if (mainGridApiRef?.current) {
            mainGridApiRef.current.showNoRowsOverlay();
          }
        });
    },
    [dispatch]
  );

  const searchFn = () => {
    pageRef.current = 0;
    const dataSource = { getRows };
    if (mainGridApiRef.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption('datasource', dataSource);
    }
  };

  const handleSearch = useDebounce(() => {
    pageRef.current = 0;
    searchFn();
  }, 500);

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    handleSearch(searchValue);
  };

  const runWaitingTimeInterval = () => {
    if (getData('adminBrandLastSync')) {
      const value = JSON.parse(getData('adminBrandLastSync'));
      waitingTimeInterval.current = setInterval(() => {
        const diffInMin = Math.floor(((Date.now() - value) / 1000 / 60) << 0);
        if (diffInMin > 2) {
          setWaitingTime('');
          clearInterval(waitingTimeInterval.current);
        }
        let waitingMsg = '';
        if (diffInMin <= 2) waitingMsg = 'cooldown';
        setWaitingTime(waitingMsg);
      }, 2000);
    }
  };

  const onSyncBlazeBrands = () => {
    if (!syncBrandLoader && !waitingTime) {
      dispatch(syncBrand())
        .unwrap()
        .then((res) => {
          setData('adminBrandLastSync', Date.now());
          setWaitingTime('Wait for 2 min');
          runWaitingTimeInterval();
          dispatch(
            openSnackbar({
              content: res?.message || 'Brands synced successfully',
              color: 'success',
              icon: 'check',
            })
          );
          searchFn();
        })
        .catch((err) => {
          dispatch(
            openSnackbar({
              content: err?.message || err?.error || 'Failed to sync brands',
              color: 'error',
              icon: 'warning',
            })
          );
        });
    }
  };

  const onRowClicked = (row) => {
    if (row?.colDef?.headerName === 'Action') return;
    const id = row?.data?._id || row?.data?.brandId;
    if (id) navigate(`${BASE_PATH}/${id}`);
  };

  const onClickEdit = (props) => {
    const id = props?.data?._id;
    const brandId = props?.data?.brandId;
    if (id && brandId) {
      navigate(`${BASE_PATH}/edit?id=${encodeURIComponent(id)}&brandId=${encodeURIComponent(brandId)}`);
    } else if (id) {
      navigate(`${BASE_PATH}/edit?id=${encodeURIComponent(id)}&brandId=${encodeURIComponent(id)}`);
    }
  };

  const deleteHandler = (brandId) => {
    dispatch(setModalLoader(true));
    dispatch(deleteBrand(brandId))
      .unwrap()
      .then((res) => {
        dispatch(openSnackbar({ content: res?.message || 'Brand deleted successfully', color: 'success', icon: 'check' }));
        dispatch(closeCustomModalDialog());
        pageRef.current = 0;
        searchFn();
      })
      .catch((err) => {
        dispatch(setModalLoader(false));
        dispatch(openSnackbar({
          content: err?.message || err?.error || 'Failed to delete brand',
          color: 'error',
          icon: 'warning',
        }));
      });
  };

  const applyBrandFilter = (filterPayload) => {
    pageRef.current = 0;
    brandFiltersPayload.current = filterPayload;
    if (!mainGridApiRef.current) return;
    const dataSource = { getRows };
    mainGridApiRef.current.ensureIndexVisible(0, null);
    mainGridApiRef.current.setGridOption('datasource', null);
    requestAnimationFrame(() => {
      if (mainGridApiRef.current) {
        mainGridApiRef.current.setGridOption('datasource', dataSource);
      }
    });
  };

  useEffect(() => {
    runWaitingTimeInterval();
    return () => {
      if (waitingTimeInterval.current) clearInterval(waitingTimeInterval.current);
    };
  }, []);

  const lastBrandSyncLocal = getData('adminBrandLastSync');
  const lastBrandSyncDate = lastBrandSyncLocal
    ? new Date(JSON.parse(lastBrandSyncLocal))
    : '';
  const lastBrandSyncDisplay = lastBrandSyncDate
    ? lastBrandSyncDate.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
    : '';

  return (
    <CommonLayout>
      <Box sx={{ mt: 2 }}>
        <div className="last-sync">
          <div>Last Sync : {lastBrandSyncDisplay || 'Syncing Now'}</div>
          <LightTooltip
            TransitionComponent={Zoom}
            title={`${
              syncBrandLoader
                ? 'Syncing is in Progress'
                : waitingTime
                ? 'Please wait for next sync'
                : 'Sync brands from blaze'
            }`}
          >
            <div
              className={`product-sync-div ${waitingTime ? 'disabled-text' : ''}`}
              onClick={onSyncBlazeBrands}
            >
              {syncBrandLoader ? (
                <CircularProgress size={18} />
              ) : waitingTime ? (
                'Sync Brands'
              ) : (
                'Sync Brands'
              )}
            </div>
          </LightTooltip>
        </div>
      </Box>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} pb={{ lg: 2 }} className="row-pointer" position="relative">
        <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={2}>
          <Grid item xs={12} sm={2}>
            <Typography variant="h4">Brands</Typography>
          </Grid>
          <Grid item xs={12} sm={10}>
            <Stack
              direction="row"
              justifyContent={{ xs: 'start', sm: 'end' }}
              flexWrap="wrap"
              alignItems="stretch"
              gap={2}
            >
              <TextField
                className="custom-search-field"
                placeholder="Search"
                label=""
                value={searchTerm}
                onChange={(e) => searchHandler(e.target.value)}
                sx={{ minWidth: '280px' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton size="small">
                        <SearchIcon width="18px" height="18px" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  endAdornment:
                    searchTerm?.length > 0 ? (
                      <InputAdornment position="end">
                        <IconButton onClick={() => searchHandler('')} size="small">
                          <CloseRounded />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                }}
              />
              <Link to={`${BASE_PATH}/add`}>
                <MDButton
                  variant="contained"
                  color="primary"
                  style={{ height: '100%', minWidth: '140px' }}
                  startIcon={<AddRoundedIcon fontSize="large" />}
                >
                  Add Brand
                </MDButton>
              </Link>
            </Stack>
          </Grid>
        </Grid>
        <FilterBrands applyBrandFilter={applyBrandFilter} />
        <AgGridTable
          col={columnDefs}
          mainGridApiRef={mainGridApiRef}
          getRows={getRows}
          pageRef={pageRef}
          isLoading={loading}
          onRowClicked={onRowClicked}
          style={{ height: '56vh' }}
          gridOptions={{ rowHeight: 64 }}
        />
      </Box>
    </CommonLayout>
  );
}
